import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import css from './FavoriteButton.module.css';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { handleToggleFavorites } from '../../containers/ListingPage/ListingPage.shared';
import {
  profileUpdateInProgressSelector,
  updateProfile,
} from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';
import { useIntl } from '../../util/reactIntl';

/**
 * The heart: an outline, filled when the listing is saved.
 *
 * @param {Object} props
 * @param {string} props.className
 * @returns {JSX.Element}
 */
export const HeartIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M12 20.3s-7.4-4.5-9.2-9.2C1.6 7.9 3.7 4.8 7 4.8c2 0 3.6 1.1 5 3 1.4-1.9 3-3 5-3 3.3 0 5.4 3.1 4.2 6.3-1.8 4.7-9.2 9.2-9.2 9.2z" />
  </svg>
);

const FavoriteButton = props => {
  const { isAuthenticated } = useSelector(state => state.auth);
  const { currentUser } = useSelector(state => state.user);
  const history = useHistory();
  const dispatch = useDispatch();
  const routeConfiguration = useRouteConfiguration();
  const profileUpdateInProgress = useSelector(profileUpdateInProgressSelector);
  const intl = useIntl();

  const {
    rootClassName,
    className,
    iconClassName,
    listingId,
    isVisible,
    listingAuthor,
    renderCustomButton,
  } = props;

  const currentListingId = listingId?.uuid ?? listingId;
  const favorites = currentUser?.attributes.profile.privateData.favorites ?? [];
  const [isFavorite, setIsFavorite] = useState(favorites.includes(currentListingId));

  const classes = rootClassName || classNames(css.root, className);
  const iconClasses = classNames(iconClassName, {
    [css.icon]: !iconClassName,
    [css.bookmarked]: isFavorite,
  });

  const isComponentDisabled =
    !isVisible ||
    !currentListingId ||
    !currentUser ||
    !isAuthenticated ||
    listingAuthor?.id?.uuid === currentUser?.id?.uuid;

  const handleClick = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      if (profileUpdateInProgress) {
        return;
      }

      if (!isAuthenticated) {
        history.push(createResourceLocatorString('LoginPage', routeConfiguration, {}, {}));
        return;
      }

      setIsFavorite(!isFavorite);

      handleToggleFavorites({
        currentUser,
        routes: routeConfiguration,
        location: history.location,
        history,
        params: { id: currentListingId },
        onUpdateFavorites: payload => dispatch(updateProfile(payload)),
      })(isFavorite);
    },
    [isFavorite, currentUser, profileUpdateInProgress, isAuthenticated]
  );

  useEffect(() => {
    setIsFavorite(isFavorite);
  }, [isFavorite]);

  if (isComponentDisabled) {
    return null;
  }
  if (typeof renderCustomButton === 'function') {
    return renderCustomButton({
      onClick: handleClick,
      isFavorite,
      isComponentDisabled,
      profileUpdateInProgress,
      currentListingId,
      currentUser,
      isAuthenticated,
    });
  }

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? handleClick(e) : null)}
      aria-pressed={isFavorite}
      aria-label={intl.formatMessage({
        id: isFavorite ? 'FavoriteButton.saved' : 'FavoriteButton.save',
      })}
      aria-disabled={`${profileUpdateInProgress}`}
    >
      <HeartIcon className={iconClasses} />
    </div>
  );
};

export default FavoriteButton;
